/* eslint-disable no-confusing-arrow */
import React, { useEffect, useState } from 'react';

// React Router DOM
import { Redirect } from 'react-router-dom';

// Redux
import { useSelector, useDispatch } from 'react-redux';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Collapse from '@material-ui/core/Collapse';
import ExpandMore from '@material-ui/icons/ExpandMore';
import Typography from '@material-ui/core/Typography';

// Clsx
import clsx from 'clsx';

// Actions
import * as CollapseActions from 'store/ducks/collapse/actions';
import * as DrawerActions from 'store/ducks/drawer/actions';

// Types
import { ApplicationState } from 'store';

// Helpers
import {
  setItemOpen,
  getItemOpen,
  setCurrentPath,
  getCurrentPath,
} from 'helpers/drawer';
import { getToken, isAuthorized } from 'helpers/auth';

// Constants
import PATH from 'constants/basePath';

// Components
import { MenuItem } from '../../components';

// Styles
import useStyles from './menuCollapse.styles';

interface items {
  title: string;
  subtitle?: string;
  pathRoute?: string;
  externalPathRoute?: string;
  modules?: string[];
  types?: string[];
}

type Props = {
  modules: string[];
  types: string[];
  title: string;
  icon?: React.ReactNode;
  open?: boolean;
  arrayItems?: Array<items[]>;
  items?: {
    title: string;
    subtitle?: string;
    pathRoute?: string;
    externalPathRoute?: string;
    modules?: string[];
    types?: string[];
    moduleConsortionHasAutorization?: boolean;
  }[];
  classes?: {
    item?: string;
    title?: string;
    list?: string;
  };
  children?: React.ReactElement | React.ReactNode;
  isConsortion: boolean;
  isCompany?: boolean;
  moduleConsortionHasAutorization?: boolean;
  moduleConsortionHasAllAutorization?: boolean;
};

type State = {};

type MenuCollapseProps = Props & State;

const MenuCollapse: React.FC<MenuCollapseProps> = ({
  title,
  icon,
  open,
  items,
  modules,
  types,
  classes,
  children,
  arrayItems,
  isConsortion,
  isCompany,
  moduleConsortionHasAutorization,
  moduleConsortionHasAllAutorization,
}) => {
  const {
    collapse,
    resetPaddingItem,
    primaryScope,
    primaryItem,
    expand,
    collapseList: collapseListStyles,
    nested,
    selected,
    notSelected,
    selectedTitle,
    withoutBorder,
    reportCollapseList,
  } = useStyles();
  const dispatch = useDispatch();

  const [isCollapse, setIsCollapse] = useState({ title: '', isOpen: false });
  const [isOpen, setIsOpen] = useState(false);
  const [currentTitle, setCurrentTitle] = useState('');

  const routeActive = useSelector(
    (state: ApplicationState) => state.routes.routeActive,
  );

  const drawerOpen = useSelector(
    (state: ApplicationState) => state.drawer.open,
  );

  const collapseList = useSelector(
    (state: ApplicationState) => state.collapse.collapseList,
  );

  const handleClick = (title: string) => {
    if (!drawerOpen) {
      dispatch(
        DrawerActions.setToggleDrawer({
          open: !drawerOpen,
        }),
      );
    }

    const newCollapseList = collapseList.map((collapse) =>
      collapse.title === title
        ? { ...collapse, open: !collapse.open }
        : { ...collapse, open: false },
    );

    dispatch(
      CollapseActions.setToggleCollapse({
        collapseList: newCollapseList,
      }),
    );

    setItemOpen(title);
    setCurrentPath(title);
  };
  useEffect(() => {
    setIsOpen(isCollapse.isOpen);
    setCurrentTitle(isCollapse.title);
  }, [isCollapse.isOpen, isCollapse.title]);
  useEffect(() => {
    const collapseItemOpen = getItemOpen();
    const currentPath = window.location.pathname;
    if (collapseItemOpen) {
      const newCollapseList = collapseList.map((collapse) =>
        collapse.title === collapseItemOpen && getCurrentPath() === currentPath
          ? { ...collapse, open: true }
          : { ...collapse, open: false },
      );

      dispatch(
        CollapseActions.setToggleCollapse({
          collapseList: newCollapseList,
        }),
      );
    }

    // eslint-disable-next-line
  }, [dispatch]);

  if (!getToken()) {
    return <Redirect to={PATH.LOGIN} />;
  }

  if (
    !isAuthorized(
      modules,
      types,
      false,
      isConsortion,
      isCompany,
      !!moduleConsortionHasAutorization,
    )
  ) {
    return <></>;
  }

  return (
    <div className={clsx({ [collapse]: open })}>
      <ListItem
        button
        onClick={() => handleClick(title)}
        classes={{
          root: clsx(classes?.item, { [resetPaddingItem]: open }),
        }}>
        <ListItemText
          primary={
            <div className={primaryScope}>
              {icon || ''}
              <Typography className={[primaryItem, classes?.title].join(' ')}>
                {title || ''}
              </Typography>
            </div>
          }
        />
        {!open && drawerOpen && <ExpandMore className={expand} />}
      </ListItem>
      <Collapse in={open} timeout={{ enter: 350, exit: 450 }}>
        {title !== 'Relatórios' ? (
          <List
            className={[collapseListStyles, classes?.list].join(' ')}
            component='div'
            disablePadding>
            {!children &&
              items &&
              items.map((item, index: number) => (
                <MenuItem
                  key={`${item.pathRoute}__${item.title}`}
                  className={[
                    nested,
                    item.pathRoute === routeActive ? selected : notSelected,
                  ].join(' ')}
                  title={item.title}
                  setIsCollapse={!index && setIsCollapse}
                  isCollapse={isCollapse}
                  subtitle={item.subtitle}
                  pathRoute={item.pathRoute}
                  titleStyle={{ color: '#fff' }}
                  types={item.types ? item.types : types}
                  externalPathRoute={item.externalPathRoute}
                  modules={item.modules ? item.modules : modules}
                  isConsortion={isConsortion}
                  moduleConsortionHasAutorization={
                    !!item?.moduleConsortionHasAutorization
                  }
                  moduleConsortionHasAllAutorization={
                    !!moduleConsortionHasAllAutorization
                  }
                />
              ))}
            {children}
          </List>
        ) : (
          arrayItems &&
          arrayItems.map((items) => (
            <List
              className={
                currentTitle !== items[0].subtitle
                  ? withoutBorder
                  : reportCollapseList
              }
              component='div'
              disablePadding>
              {!children &&
                items.map(
                  (item, index: number) =>
                    (index === 0 ||
                      (isOpen && currentTitle === items[0].subtitle)) && (
                      <MenuItem
                        isConsortion={isConsortion}
                        moduleConsortionHasAllAutorization={
                          !!moduleConsortionHasAllAutorization
                        }
                        key={`${item.pathRoute}__${item.title}`}
                        className={[
                          nested,
                          isOpen &&
                          currentTitle === items[0].subtitle &&
                          !item.pathRoute
                            ? selectedTitle
                            : (item.pathRoute === routeActive &&
                                item.pathRoute &&
                                selected) ||
                              notSelected,
                        ].join(' ')}
                        title={item.title}
                        setIsCollapse={!index && setIsCollapse}
                        isCollapse={isCollapse}
                        isOpen={isOpen}
                        currentTitle={currentTitle}
                        subtitle={item.subtitle}
                        pathRoute={item.pathRoute}
                        titleStyle={{ color: '#fff' }}
                        types={item.types ? item.types : types}
                        externalPathRoute={item.externalPathRoute}
                        modules={item.modules ? item.modules : modules}
                      />
                    ),
                )}
              {children}
            </List>
          ))
        )}
      </Collapse>
    </div>
  );
};

export default MenuCollapse;
export { MenuCollapse };
